import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import {
  Loader2, Calendar, MapPin, ArrowRight, CheckCircle2, Clock,
  AlertCircle, Inbox, CalendarDays, List as ListIcon, RotateCcw,
} from "lucide-react";
import { usePortalAuth, portalApi } from "../../context/PortalAuthContext";
import PortalShell from "./PortalShell";
import SEO from "../../components/SEO";

const STATUS = {
  outstanding: { label: "Awaiting schedule", color: "#FF9500", Icon: AlertCircle },
  scheduled:   { label: "Scheduled",         color: "#0A84FF", Icon: Clock },
  in_progress: { label: "In progress",       color: "#FFCC00", Icon: Loader2 },
  completed:   { label: "Completed",         color: "#22c55e", Icon: CheckCircle2 },
  invoiced:    { label: "Invoiced",          color: "#94a3b8", Icon: CheckCircle2 },
  cancelled:   { label: "Cancelled",         color: "#737373", Icon: AlertCircle },
};

const TABS = [
  { id: "upcoming",    label: "Upcoming",    emptyTitle: "No upcoming jobs.",    emptyBody: "When we schedule a visit, it'll appear here." },
  { id: "outstanding", label: "Outstanding", emptyTitle: "Nothing outstanding.", emptyBody: "Bookings we haven't scheduled yet will land here." },
  { id: "completed",   label: "Completed",   emptyTitle: "No completed jobs yet.", emptyBody: "Once we've been out, your service history shows up here." },
];

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
  } catch { return ""; }
}
function fmtDateTime(iso) {
  try {
    return new Date(iso).toLocaleString("en-AU", {
      weekday: "short", day: "numeric", month: "short",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return ""; }
}
function fmtMoney(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(n);
}

export default function PortalHome() {
  const { customer, loading } = usePortalAuth();
  const [tab, setTab] = useState("upcoming");
  const [view, setView] = useState("list"); // list | calendar
  const [jobs, setJobs] = useState(null);
  const [counts, setCounts] = useState({ upcoming: 0, outstanding: 0, completed: 0 });
  const [error, setError] = useState("");

  // Load counts once per customer
  useEffect(() => {
    if (!customer) return;
    Promise.all(TABS.map((t) => portalApi.get(`/portal/jobs?bucket=${t.id}`)))
      .then((rs) => setCounts({
        upcoming: rs[0].data?.length || 0,
        outstanding: rs[1].data?.length || 0,
        completed: rs[2].data?.length || 0,
      }))
      .catch(() => { /* ignore */ });
  }, [customer]);

  // Load current tab
  useEffect(() => {
    if (!customer) return;
    setJobs(null);
    setError("");
    portalApi.get(`/portal/jobs?bucket=${tab}`)
      .then((r) => setJobs(r.data || []))
      .catch((err) => setError(err?.response?.data?.detail || "Couldn't load your jobs."));
  }, [customer, tab]);

  if (loading) {
    return (
      <PortalShell>
        <div className="min-h-[40vh] flex items-center justify-center">
          <Loader2 className="animate-spin text-zinc-500" />
        </div>
      </PortalShell>
    );
  }
  if (!customer) return <Navigate to="/portal/login" replace />;

  const firstName = (customer.name || "").split(" ")[0] || "there";
  const activeTab = TABS.find((t) => t.id === tab) || TABS[0];

  return (
    <PortalShell>
      <SEO
        title="My jobs · Tech Emergency portal"
        description="Your Tech Emergency service history and upcoming visits."
        path="/portal"
        noIndex
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="te-overline mb-3">/ My account</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tighter leading-tight mb-2">
          Hi, {firstName}.
        </h1>
        <p className="text-zinc-400 text-sm leading-relaxed mb-8">
          Everything we&apos;ve done for you, plus what&apos;s coming up next.
        </p>

        {/* Tabs */}
        <div className="flex flex-wrap items-center gap-2 mb-4 border-b border-white/10">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`portal-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className="px-3 py-2.5 text-sm font-medium border-b-2 transition-colors"
              style={{
                borderBottomColor: tab === t.id ? "var(--te-primary)" : "transparent",
                color: tab === t.id ? "#fff" : "var(--te-text-muted)",
              }}
            >
              {t.label} <span className="text-[11px] text-zinc-500">({counts[t.id]})</span>
            </button>
          ))}
          <div className="ml-auto flex items-center gap-1">
            <button
              data-testid="portal-view-list"
              onClick={() => setView("list")}
              className="px-2.5 py-1.5 text-[11px] te-overline"
              style={{
                background: view === "list" ? "var(--te-primary)" : "transparent",
                color: view === "list" ? "#fff" : "var(--te-text-muted)",
              }}
            >
              <ListIcon size={11} className="inline mr-1" /> List
            </button>
            <button
              data-testid="portal-view-calendar"
              onClick={() => setView("calendar")}
              className="px-2.5 py-1.5 text-[11px] te-overline"
              style={{
                background: view === "calendar" ? "var(--te-primary)" : "transparent",
                color: view === "calendar" ? "#fff" : "var(--te-text-muted)",
              }}
            >
              <CalendarDays size={11} className="inline mr-1" /> Calendar
            </button>
          </div>
        </div>

        {jobs === null && !error && (
          <div className="te-card p-6 flex items-center gap-2 text-zinc-500 text-sm">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        )}

        {error && (
          <div className="te-card p-6 border border-red-500/30 bg-red-500/10 text-red-300 text-sm flex items-start gap-2">
            <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {jobs && jobs.length === 0 && (
          <div className="te-card p-10 text-center" data-testid={`portal-empty-${tab}`}>
            <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center"
                 style={{ background: "var(--te-primary)", opacity: 0.85 }}>
              <Inbox size={22} color="#fff" />
            </div>
            <h3 className="text-lg font-bold tracking-tight mb-1">{activeTab.emptyTitle}</h3>
            <p className="text-sm text-zinc-400 leading-relaxed">{activeTab.emptyBody}</p>
          </div>
        )}

        {jobs && jobs.length > 0 && view === "list" && (
          <ul className="space-y-3" data-testid="portal-jobs-list">
            {jobs.map((j) => <JobCard key={j.id} job={j} bucket={tab} />)}
          </ul>
        )}

        {jobs && jobs.length > 0 && view === "calendar" && (
          <PortalMiniCalendar jobs={jobs} />
        )}
      </div>
    </PortalShell>
  );
}

function JobCard({ job, bucket }) {
  const meta = STATUS[job.status] || STATUS.scheduled;
  const StatusIcon = meta.Icon;
  const dateToShow = bucket === "completed" ? job.completed_at : job.scheduled_at;
  const navigate = useNavigate();
  const open = () => navigate(`/portal/jobs/${job.id}`);
  const onKey = (e) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(); }
  };
  return (
    <li>
      <div
        role="link"
        tabIndex={0}
        onClick={open}
        onKeyDown={onKey}
        data-testid={`portal-job-${job.id}`}
        className="te-card p-5 flex flex-col sm:flex-row sm:items-center gap-4 group hover:border-white/30 transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-[var(--te-primary)]"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1.5">
            <div className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] tracking-[0.15em] font-bold uppercase"
                 style={{ background: `${meta.color}1f`, color: meta.color }}>
              <StatusIcon size={9} />
              {meta.label}
            </div>
            {dateToShow && (
              <span className="text-[11px] text-zinc-500 flex items-center gap-1">
                <Calendar size={10} /> {fmtDateTime(dateToShow)}
              </span>
            )}
          </div>
          <div className="text-base font-semibold leading-snug group-hover:text-[var(--te-primary)] transition-colors">
            {job.service}
          </div>
          {job.suburb && (
            <div className="text-[11px] text-zinc-500 mt-1 inline-flex items-center gap-1">
              <MapPin size={10} /> {job.suburb}
            </div>
          )}
        </div>
        <div className="flex items-center gap-4 sm:gap-6 flex-shrink-0">
          {bucket === "completed" && (
            <BookAgain service={job.service} suburb={job.suburb} />
          )}
          <div className="text-right">
            <div className="text-[9px] tracking-[0.18em] uppercase text-zinc-500 mb-0.5">Total</div>
            <div className="text-base font-bold tracking-tight">{fmtMoney(job.cost_total)}</div>
          </div>
          <ArrowRight size={16} className="text-zinc-500 group-hover:text-white transition" />
        </div>
      </div>
    </li>
  );
}

function BookAgain({ service, suburb }) {
  // Prefill the public booking form. Clicking propagates only the search params.
  const qs = new URLSearchParams({ service: service || "", suburb: suburb || "" }).toString();
  return (
    <Link
      to={`/book?${qs}`}
      data-testid="portal-book-again"
      onClick={(e) => e.stopPropagation()}
      className="text-[11px] te-overline px-2.5 py-1.5 hover:bg-white/5"
      style={{ border: "1px solid var(--te-border)" }}
    >
      <RotateCcw size={10} className="inline mr-1" /> Book again
    </Link>
  );
}

// Lightweight read-only mini calendar — month grid, no library needed for portal side.
function PortalMiniCalendar({ jobs }) {
  const [month, setMonth] = useState(() => {
    const d = new Date();
    d.setDate(1); d.setHours(0,0,0,0);
    return d;
  });

  const events = useMemo(() => {
    const map = {};
    for (const j of jobs) {
      const iso = j.scheduled_at || j.completed_at;
      if (!iso) continue;
      const d = new Date(iso);
      if (isNaN(d.getTime())) continue;
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map[key]) map[key] = [];
      map[key].push(j);
    }
    return map;
  }, [jobs]);

  const year = month.getFullYear();
  const m = month.getMonth();
  const firstDayWeekday = new Date(year, m, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(year, m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDayWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const nav = (delta) => {
    const d = new Date(year, m + delta, 1);
    setMonth(d);
  };

  return (
    <div data-testid="portal-mini-calendar">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => nav(-1)} className="te-btn te-btn-ghost text-xs" data-testid="portal-cal-prev">‹ Prev</button>
        <div className="font-semibold tracking-tight">
          {month.toLocaleString("en-AU", { month: "long", year: "numeric" })}
        </div>
        <button onClick={() => nav(1)} className="te-btn te-btn-ghost text-xs" data-testid="portal-cal-next">Next ›</button>
      </div>
      <div className="grid grid-cols-7 gap-px" style={{ background: "var(--te-border)" }}>
        {["S","M","T","W","T","F","S"].map((d, i) => (
          <div key={i} className="text-center text-[10px] py-2 te-overline" style={{ background: "var(--te-bg)" }}>{d}</div>
        ))}
        {cells.map((d, idx) => {
          const key = d ? `${year}-${m}-${d}` : null;
          const dayEvents = key ? (events[key] || []) : [];
          return (
            <div key={idx} className="min-h-[64px] p-1.5 text-[11px]" style={{ background: "var(--te-bg)" }}>
              {d && <div className="text-zinc-500 mb-1">{d}</div>}
              {dayEvents.slice(0,2).map((j) => {
                const meta = STATUS[j.status] || STATUS.scheduled;
                return (
                  <Link
                    key={j.id}
                    to={`/portal/jobs/${j.id}`}
                    className="block truncate px-1.5 py-0.5 mb-0.5 text-[10px] font-medium hover:opacity-90"
                    style={{ background: `${meta.color}33`, color: meta.color }}
                  >
                    {j.service}
                  </Link>
                );
              })}
              {dayEvents.length > 2 && (
                <div className="text-[9px] text-zinc-500">+{dayEvents.length - 2} more</div>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-3 text-[11px] text-zinc-500">
        Tip: scheduled visits appear by date. Completed jobs show on the date we wrapped up.
      </div>
    </div>
  );
}

// Re-export to avoid breaking older imports from /portal index files (if any).
export { fmtDate, fmtMoney };
